import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Icon, Select, Toast } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { campaignsService, type Campaign, type Segment } from "@/api/campaigns.service";
import {
  mailingGroupsService,
  type MailingGroupResponseSummary,
} from "@/api/mailing-groups.service";
import { templates as templatesApi } from "@/api/templates.service";
import { campaignCoverageLabel, campaignStatusChip, campaignTypeLabel } from "./campaign-display";
import { RecipientsProjection } from "./RecipientsProjection";
import { CreateSegmentModal } from "./CreateSegmentModal";
import { SegmentsList } from "./SegmentsList";
import { EMPTY_SELECTION, type RecipientSelection } from "./selection";
import { ROUTES } from "@/routes";
import styles from "./MailingGroups.module.css";

function CampaignDetail() {
  const {
    publicationId = "",
    edition = "",
    groupId = "",
    campaignId = "",
  } = useParams<{
    publicationId: string;
    edition: string;
    groupId: string;
    campaignId: string;
  }>();

  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [selection, setSelection] = useState<RecipientSelection>(EMPTY_SELECTION);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<"candidates" | "sends">("candidates");
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [sendsCount, setSendsCount] = useState(0);

  // --- Template assignment (inline edit) ---
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesHelperText, setTemplatesHelperText] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Resolved SendGrid template NAME for the assigned template. The campaign API
  // only returns `template_name`, which falls back to the raw sendgrid id when
  // the local row has no name — so we fetch the typed template list once (on
  // load) and resolve the real name for display in the header + send UIs.
  const [templateDisplayName, setTemplateDisplayName] = useState<string | null>(null);

  // --- Reminder audience context (response summary) ---
  const [responseSummary, setResponseSummary] = useState<MailingGroupResponseSummary | null>(null);

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCampaign(await campaignsService.getById(campaignId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load campaign.");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void loadCampaign();
  }, [loadCampaign]);

  const pushToast = useCallback((message: string, type: ToastType) => {
    setToast({ message, type });
  }, []);

  // For a reminder, fetch the group's response summary so the note can quote the
  // real responded / non-responder counts. Failure falls back to the static note.
  useEffect(() => {
    if (!campaign || campaign.type !== "reminder" || !groupId) return;
    let cancelled = false;
    mailingGroupsService
      .getResponseSummary(groupId)
      .then(summary => {
        if (!cancelled) setResponseSummary(summary);
      })
      .catch(() => {
        // Graceful fallback: keep the static reminder note.
      });
    return () => {
      cancelled = true;
    };
  }, [campaign, groupId]);

  // Resolve the assigned template's real SendGrid name for display. We fetch the
  // typed list once the campaign (with a template_id) is available and match by
  // id. Unlike the edit dropdown we do NOT filter out archived / missing rows —
  // an assigned template must still show its name even if it was later archived.
  // A stale response (campaign or template_id changed) is ignored, and the fetch
  // is keyed off template_id so it doesn't re-run on every render.
  useEffect(() => {
    const templateId = campaign?.template_id;
    if (!templateId) {
      setTemplateDisplayName(null);
      return;
    }
    let cancelled = false;
    templatesApi
      .list({ type: campaign?.type === "reminder" ? "reminder" : "survey_request" })
      .then(res => {
        if (cancelled) return;
        const match = res.templates.find(t => t.id === templateId);
        setTemplateDisplayName(
          match ? (match.name ?? match.subject ?? match.sendgrid_template_id) : null,
        );
      })
      .catch(() => {
        // Graceful fallback: keep campaign.template_name (raw id) as the label.
        if (!cancelled) setTemplateDisplayName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign?.template_id, campaign?.type]);

  // Lazily load templates (typed to the campaign) when the operator opens the
  // inline editor — mirrors the Start/Add-reminder modal loading pattern.
  const openTemplateEditor = useCallback(async () => {
    if (!campaign) return;
    setEditingTemplate(true);
    setSelectedTemplateId(campaign.template_id ?? "");
    setTemplateOptions([]);
    setTemplatesLoading(true);
    setTemplatesHelperText("Loading templates…");
    try {
      const res = await templatesApi.list({
        type: campaign.type === "reminder" ? "reminder" : "survey_request",
      });
      const usable = res.templates
        .filter(t => t.id && !t.archived && !t.missing_in_sendgrid)
        .map(t => ({
          value: t.id as string,
          label: t.name ?? t.subject ?? t.sendgrid_template_id,
        }));
      setTemplateOptions(usable);
      setTemplatesHelperText(usable.length === 0 ? "No templates available" : "");
    } catch {
      setTemplateOptions([]);
      setTemplatesHelperText("Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  }, [campaign]);

  const saveTemplate = useCallback(async () => {
    if (!selectedTemplateId) return;
    setSavingTemplate(true);
    try {
      await campaignsService.update(campaignId, { templateId: selectedTemplateId });
      setEditingTemplate(false);
      pushToast("Template assigned.", "success");
      await loadCampaign();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : "Failed to assign template.", "error");
    } finally {
      setSavingTemplate(false);
    }
  }, [selectedTemplateId, campaignId, loadCampaign, pushToast]);

  // After a send is dispatched, its targets flip new → scheduled in the
  // projection, so we refetch both lists (arch-discussion §19.2). Bumping the
  // shared refreshKey also clears the projection's selection back to empty.
  // The operator then lands on the Sends tab to see the new send. The segment
  // arrives already dispatched (sending or scheduled), so the toast reflects
  // that outcome rather than a resting draft.
  const handleCreated = useCallback(
    (segment: Segment, count: number) => {
      setShowCreateModal(false);
      setRefreshKey(n => n + 1);
      setActiveTab("sends");
      const message = segment.scheduled_at
        ? `Send scheduled (id ${segment.id}, ${count} recipients frozen).`
        : `Sending now (id ${segment.id}, ${count} recipients).`;
      pushToast(message, "success");
    },
    [pushToast],
  );

  const breadcrumb = (
    <p className={styles.breadcrumb}>
      <Link
        to={ROUTES.surveys.campaignGroupDetail(publicationId, edition, groupId)}
        className={styles.breadcrumbLink}
      >
        Back to Campaigns
      </Link>
    </p>
  );

  if (loading) {
    return (
      <div className={styles.wrapper}>
        {breadcrumb}
        <LoadingState message="Loading campaign..." />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className={styles.wrapper}>
        {breadcrumb}
        <ErrorBanner
          error={error ?? "Campaign not found."}
          onRetry={loadCampaign}
          loading={loading}
        />
      </div>
    );
  }

  const templateMissing = !campaign.template_id;
  const statusChip = campaignStatusChip(campaign.effective_status);
  const coverage = campaignCoverageLabel(campaign);
  // Prefer the resolved SendGrid name; fall back to the campaign's (raw) name
  // while the list is still loading or can't resolve the assigned template.
  const templateLabel = templateDisplayName ?? campaign.template_name ?? "Assigned";

  return (
    <div className={styles.wrapper}>
      <PageHeader title={`${campaign.name} - Details`}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<Icon icon="chevron-left" size="small" />}
          onClick={() =>
            navigate(ROUTES.surveys.campaignGroupDetail(publicationId, edition, groupId))
          }
        >
          Back to Campaigns
        </Button>
      </PageHeader>

      {breadcrumb}

      <div className={styles.recipientsHeaderRow}>
        <span className={styles.statusChip}>
          <span className={styles.campaignMetaLabel}>Type: {campaignTypeLabel(campaign.type)}</span>
        </span>
        <span className={styles.statusChip}>
          <Chip
            size="small"
            color={statusChip.color}
            className={statusChip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span className={styles.statusChip}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: statusChip.dotColor }}
                aria-hidden="true"
              />
              {statusChip.label}
            </span>
          </Chip>
          {coverage && <span className={styles.mutedNote}>{coverage}</span>}
        </span>
      </div>

      {/* Template row: shows the assigned template and an inline assign/change
          editor. A campaign can't be dispatched without a template (FIX A). */}
      <div className={styles.recipientsHeaderRow}>
        {editingTemplate ? (
          <div className={styles.campaignsActions}>
            <div className={styles.filterField}>
              <Select
                label="Template"
                options={[{ value: "", label: "Select a template…" }, ...templateOptions]}
                value={selectedTemplateId}
                onChange={e => setSelectedTemplateId(e.target.value)}
                disabled={templatesLoading}
                helperText={templatesHelperText || undefined}
                fullWidth
              />
            </div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={savingTemplate || !selectedTemplateId}
              onClick={() => void saveTemplate()}
            >
              {savingTemplate ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="outlined"
              color="default"
              size="small"
              disabled={savingTemplate}
              onClick={() => setEditingTemplate(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <span className={styles.statusChip}>
            <span className={styles.campaignMetaLabel}>
              {/* Key the label off template_id (like the button) so an assigned
                  template with a null name never contradictorily reads
                  "Not assigned" next to a "Change" button. Shows the resolved
                  SendGrid name (templateLabel) rather than the raw id. */}
              Template: {campaign.template_id ? templateLabel : "Not assigned"}
            </span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => void openTemplateEditor()}
            >
              {campaign.template_id ? "Change" : "Assign template"}
            </Button>
          </span>
        )}
      </div>

      {templateMissing && (
        <p className={styles.warningBanner}>
          This campaign has no template — assign one before sending.
        </p>
      )}

      {campaign.type === "reminder" &&
        (responseSummary ? (
          <p className={styles.mutedNote}>
            Reminders go only to referees who haven't responded yet —{" "}
            {responseSummary.responded.toLocaleString()} of{" "}
            {responseSummary.totalReferees.toLocaleString()} have responded,{" "}
            {responseSummary.nonResponders.toLocaleString()} still to remind.
          </p>
        ) : (
          <p className={styles.mutedNote}>
            Reminders go only to referees who haven't responded yet.
            {candidatesTotal > 0 &&
              ` This audience is narrowed automatically to ${candidatesTotal.toLocaleString()} non-responder(s).`}
          </p>
        ))}

      {campaign.status === "synchronizing" && (
        <p className={styles.mutedNote}>
          Recipients are still synchronizing — the projection may be empty until the campaign has
          started.
        </p>
      )}

      <div className={styles.tabs}>
        <button
          type="button"
          className={activeTab === "candidates" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("candidates")}
        >
          Candidates <span className={styles.tabCount}>{candidatesTotal.toLocaleString()}</span>
        </button>
        <button
          type="button"
          className={activeTab === "sends" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("sends")}
        >
          Sends <span className={styles.tabCount}>{sendsCount.toLocaleString()}</span>
        </button>
      </div>

      {/* Both panels stay mounted so switching tabs never drops the operator's
          selection; only their visibility toggles. The sticky action bar lives
          inside the Candidates panel, so it hides with it on the Sends tab. */}
      <div
        className={
          activeTab === "candidates"
            ? `${styles.panel} ${styles.candidatesPanel}`
            : styles.panelHidden
        }
      >
        <p className={styles.mutedNote}>
          Select recipients below and create a Send to dispatch emails in batches.
        </p>
        <RecipientsProjection
          campaignId={campaignId}
          onSelectionChange={setSelection}
          onCreateSegment={() => setShowCreateModal(true)}
          onToast={pushToast}
          onTotalChange={setCandidatesTotal}
          refreshKey={refreshKey}
        />
      </div>

      <div className={activeTab === "sends" ? styles.panel : styles.panelHidden}>
        <SegmentsList
          campaignId={campaignId}
          refreshKey={refreshKey}
          onToast={pushToast}
          onCountChange={setSendsCount}
          templateMissing={templateMissing}
          templateName={campaign.template_id ? templateLabel : campaign.template_name}
        />
      </div>

      {showCreateModal && (
        <CreateSegmentModal
          campaignId={campaignId}
          selection={selection}
          templateName={campaign.template_id ? templateLabel : campaign.template_name}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default CampaignDetail;
